import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { CloseRounded, Menu as MenuIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import KesariInbound from '../../assets/images/Kesari-Inbound-Logo.png';
import '../Header/Header.css';

const DynamicDestinationHeader = () => {
  const SCROLL_OFFSET = 100;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pathParts = pathname.split('/').filter(Boolean);
  const slug = pathParts[1] || '';
  const destinationPath = `/destination/${slug}`;

  const [activeTab, setActiveTab] = useState('home');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hasPackages, setHasPackages] = useState(false);
  const [hasGallery, setHasGallery] = useState(false);

  const isProgrammaticScroll = useRef(false);
  const programmaticScrollTimer = useRef(null);
  const activeTabRef = useRef('home');

  const setTab = useCallback((tab) => {
    activeTabRef.current = tab;
    setActiveTab(tab);
  }, []);

  const updateSectionAvailability = useCallback(() => {
    setHasPackages(!!document.getElementById('packagesSection'));
    setHasGallery(!!document.getElementById('photoGallerySection'));
  }, []);

  useEffect(() => {
    updateSectionAvailability();
    const observer = new MutationObserver(updateSectionAvailability);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [pathname, updateSectionAvailability]);

  useEffect(() => {
    setTab('home');
    isProgrammaticScroll.current = false;

    const handleScroll = () => {
      if (isProgrammaticScroll.current) return;

      updateSectionAvailability();

      const packageSection = document.getElementById('packagesSection');
      const gallerySection = document.getElementById('photoGallerySection');
      const reviewSection = document.getElementById('reviewCard');

      if (window.scrollY < SCROLL_OFFSET) {
        setTab('home');
        return;
      }

      const atPageBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 80;
      if (atPageBottom && reviewSection) {
        setTab('reviews');
        return;
      }

      const sections = [];
      if (packageSection) sections.push({ el: packageSection, tab: 'customized' });
      if (gallerySection) sections.push({ el: gallerySection, tab: 'gallery' });
      if (reviewSection) sections.push({ el: reviewSection, tab: 'reviews' });

      let currentTab = 'home';
      for (const section of sections) {
        if (section.el.getBoundingClientRect().top <= SCROLL_OFFSET) {
          currentTab = section.tab;
        }
      }
      setTab(currentTab);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    if (window.scrollY > SCROLL_OFFSET) {
      handleScroll();
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (programmaticScrollTimer.current) {
        clearTimeout(programmaticScrollTimer.current);
      }
    };
  }, [pathname, updateSectionAvailability, setTab]);

  const scrollToSectionById = useCallback(
    (sectionId, tab) => {
      const element = document.getElementById(sectionId);
      if (!element) return;

      // Immediately set the tab (visual feedback)
      setTab(tab);

      // Lock scroll listener
      isProgrammaticScroll.current = true;
      if (programmaticScrollTimer.current) {
        clearTimeout(programmaticScrollTimer.current);
      }

      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const targetScrollTop = elementPosition - SCROLL_OFFSET;

      window.scrollTo({ top: targetScrollTop, behavior: 'smooth' });

      const unlockScroll = () => {
        isProgrammaticScroll.current = false;
      };

      if ('onscrollend' in window) {
        window.addEventListener('scrollend', unlockScroll, { once: true });
        // Safety timeout fallback
        programmaticScrollTimer.current = setTimeout(() => {
          isProgrammaticScroll.current = false;
        }, 1500);
      } else {
        // Fallback: estimate scroll duration based on distance
        const distance = Math.abs(window.scrollY - targetScrollTop);
        const duration = Math.min(Math.max(distance / 2, 600), 1200);
        programmaticScrollTimer.current = setTimeout(unlockScroll, duration);
      }
    },
    [setTab]
  );

  const scrollToTopSmooth = useCallback(() => {
    setTab('home');

    isProgrammaticScroll.current = true;
    if (programmaticScrollTimer.current) {
      clearTimeout(programmaticScrollTimer.current);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });

    const unlockScroll = () => {
      isProgrammaticScroll.current = false;
    };

    if ('onscrollend' in window) {
      window.addEventListener('scrollend', unlockScroll, { once: true });
      programmaticScrollTimer.current = setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 1500);
    } else {
      const distance = window.scrollY;
      const duration = Math.min(Math.max(distance / 2, 600), 1200);
      programmaticScrollTimer.current = setTimeout(unlockScroll, duration);
    }
  }, [setTab]);

  const closeDrawerAndScroll = useCallback(
    (sectionId, tab) => {
      setDrawerOpen(false);
      setTimeout(() => {
        if (sectionId === 'top') {
          scrollToTopSmooth();
        } else {
          scrollToSectionById(sectionId, tab);
        }
      }, 350);
    },
    [scrollToSectionById, scrollToTopSmooth]
  );

  const handleHomeClick = useCallback(() => {
    setDrawerOpen(false);
    if (isMobile) {
      closeDrawerAndScroll('top', 'home');
    } else {
      scrollToTopSmooth();
    }
  }, [isMobile, closeDrawerAndScroll, scrollToTopSmooth]);

  const handleCustomizedClick = useCallback(() => {
    if (pathname !== destinationPath) {
      sessionStorage.setItem('dynamicDestinationMenu', 'customized-holidays');
      navigate(destinationPath);
      setDrawerOpen(false);
      return;
    }
    if (isMobile) {
      closeDrawerAndScroll('packagesSection', 'customized');
    } else {
      setDrawerOpen(false);
      scrollToSectionById('packagesSection', 'customized');
    }
  }, [pathname, destinationPath, isMobile, navigate, closeDrawerAndScroll, scrollToSectionById]);

  const handlePhotoGalleryClick = useCallback(() => {
    if (pathname !== destinationPath) {
      sessionStorage.setItem('dynamicDestinationMenu', 'photo-gallery');
      navigate(destinationPath);
      setDrawerOpen(false);
      return;
    }
    if (isMobile) {
      closeDrawerAndScroll('photoGallerySection', 'gallery');
    } else {
      setDrawerOpen(false);
      scrollToSectionById('photoGallerySection', 'gallery');
    }
  }, [pathname, destinationPath, isMobile, navigate, closeDrawerAndScroll, scrollToSectionById]);

  const handleReviewsClick = useCallback(() => {
    if (pathname !== destinationPath) {
      sessionStorage.setItem('dynamicDestinationMenu', 'reviews');
      navigate(destinationPath);
      setDrawerOpen(false);
      return;
    }
    if (isMobile) {
      closeDrawerAndScroll('reviewCard', 'reviews');
    } else {
      setDrawerOpen(false);
      scrollToSectionById('reviewCard', 'reviews');
    }
  }, [pathname, destinationPath, isMobile, navigate, closeDrawerAndScroll, scrollToSectionById]);

  const isTabActive = (tab) => activeTab === tab;

  const tabButtonStyles = (isActive) => ({
    color: '#000000',
    borderRadius: 0,
    textTransform: 'none',
    fontFamily: 'gothamBold',
    fontSize: { xs: '13px', sm: '14px', md: '15px' },
    height: '80px',
    position: 'relative',
    px: 2,
    '&:hover': {
      backgroundColor: 'transparent',
      color: '#E41E25'
    },
    '&::after': isActive
      ? {
          content: '""',
          position: 'absolute',
          bottom: '15px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '85%',
          height: '3px',
          backgroundColor: '#E41E25'
        }
      : {}
  });

  const isDestinationLanding = pathname === destinationPath;
  const showPackagesNav = hasPackages || !isDestinationLanding;
  const showGalleryNav = hasGallery || !isDestinationLanding;

  const navItems = [
    { key: 'home', label: 'Home', onClick: handleHomeClick, visible: true },
    {
      key: 'customized',
      label: 'Customized Holidays',
      onClick: handleCustomizedClick,
      visible: showPackagesNav
    },
    {
      key: 'gallery',
      label: 'Photo Gallery',
      onClick: handlePhotoGalleryClick,
      visible: showGalleryNav
    },
    { key: 'reviews', label: 'Reviews', onClick: handleReviewsClick, visible: true }
  ];

  const visibleNavItems = navItems.filter((item) => item.visible);

  return (
    <Box
      className="header-container header-container-kesari"
      sx={{ zIndex: isMobile ? 1000 : 5000 }}>
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          height: '80px',
          marginLeft: { md: '0px', lg: '95px' },
          px: { xs: 1, md: 0 }
        }}>
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setDrawerOpen(true)}
            style={{ color: '#000000B2' }}>
            <MenuIcon />
          </IconButton>
        )}

        <Box
          sx={{ cursor: 'pointer', flexShrink: 0, flexGrow: isMobile ? 0 : 0.3 }}
          onClick={() => navigate('/')}>
          <Box
            component="img"
            src={KesariInbound}
            alt="Kesari Inbound Logo"
            sx={{
              height: { xs: 80, sm: 80 },
              width: { xs: 190, sm: 230 },
              objectFit: 'contain'
            }}
          />
        </Box>

        {!isMobile && (
          <Box
            display="flex"
            alignItems="center"
            flexGrow={1}
            sx={{ gap: { xs: 2, md: 4, lg: 6 }, height: '80px' }}>
            <Button onClick={handleHomeClick} sx={tabButtonStyles(isTabActive('home'))}>
              Home
            </Button>
            {showPackagesNav && (
              <Button
                onClick={handleCustomizedClick}
                sx={tabButtonStyles(isTabActive('customized'))}>
                Customized Holidays
              </Button>
            )}
            {showGalleryNav && (
              <Button
                onClick={handlePhotoGalleryClick}
                sx={tabButtonStyles(isTabActive('gallery'))}>
                Photo Gallery
              </Button>
            )}
            <Button onClick={handleReviewsClick} sx={tabButtonStyles(isTabActive('reviews'))}>
              Reviews
            </Button>
          </Box>
        )}
      </Box>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: '10px'
          }}>
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{ color: 'rgba(0, 0, 0, 0.698)', marginLeft: '5px' }}
            aria-label="close">
            <CloseRounded />
          </IconButton>
        </Box>
        <Box p={2} width="250px">
          <List>
            {visibleNavItems.map((item) => (
              <ListItem button key={item.key} onClick={item.onClick}>
                <ListItemText
                  primary={
                    <Typography component="span" sx={{ fontFamily: 'gothamBook' }}>
                      {item.label}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DynamicDestinationHeader;
